import React from 'react';
import {ForecastMapService} from '../api';
import {ForecastMapRunPalettes} from '../api/types';
import useApiClient from './useApiClient';

export type ForecastMapPalettesStatus = 'idle' | 'loading' | 'ready';

type PalettesResult = {
  runId: number | null;
  palettes: ForecastMapRunPalettes | null;
};

const EMPTY: PalettesResult = {runId: null, palettes: null};

/**
 * Fetches the alternate-palette catalog for a run (raster path only —
 * pass null while the manifest has no scalar block and nothing loads).
 * The payload is immutable for a completed run, so there is no reload
 * surface; failures resolve to null and the picker simply doesn't
 * render (the manifest legend palette still applies).
 *
 * Platform-agnostic on purpose (no DOM usage): backs the webapp picker
 * and, later, the mobile one.
 */
const useForecastMapPalettes = (runId: number | null) => {
  const {fetch} = useApiClient();
  const [result, setResult] = React.useState<PalettesResult>(EMPTY);

  React.useEffect(() => {
    if (runId === null) {
      return;
    }
    let cancelled = false;
    // silent: a failed palette fetch must never raise the global error
    // snackbar — the picker simply doesn't render and the manifest
    // legend palette still applies (same rationale as PointValue).
    fetch(ForecastMapService.forecastMapRunPalettes(runId), {silent: true})
      .then((palettes) => {
        if (!cancelled) {
          setResult({runId, palettes});
        }
      })
      .catch(() => {
        if (!cancelled) {
          setResult({runId, palettes: null});
        }
      });
    return () => {
      cancelled = true;
    };
  }, [runId, fetch]);

  const status: ForecastMapPalettesStatus =
    runId === null ? 'idle' : result.runId === runId ? 'ready' : 'loading';

  return React.useMemo(
    () => ({
      status,
      palettes:
        runId !== null && result.runId === runId ? result.palettes : null,
    }),
    [status, runId, result],
  );
};

export default useForecastMapPalettes;
